import { Box, CircularProgress, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import ActiveCampaignEmbed from './ActiveCampaignEmbed';
import FormErrorBoundary from './FormErrorBoundary';

/**
 * EarlyAccessForm component
 * Fetches the ActiveCampaign form #19 HTML from public/forms/19.txt
 * and renders it using the ActiveCampaignEmbed component wrapped in an error boundary.
 *
 * Handles loading, error, and success states with appropriate UI feedback.
 *
 * @returns {import('react').ReactElement} The early access signup form
 */
function EarlyAccessForm() {
  /** @type {[string | null, import('react').Dispatch<import('react').SetStateAction<string | null>>]} */
  const [formHtml, setFormHtml] = useState(/** @type {string | null} */ (null));
  const [loading, setLoading] = useState(true);
  /** @type {[string | null, import('react').Dispatch<import('react').SetStateAction<string | null>>]} */
  const [error, setError] = useState(/** @type {string | null} */ (null));

  useEffect(() => {
    let cancelled = false;

    const loadForm = async () => {
      try {
        const response = await fetch('/forms/19.txt');
        if (!response.ok) {
          throw new Error(`Failed to load form: ${response.status}`);
        }
        const html = await response.text();
        if (!cancelled) {
          setFormHtml(html);
          setLoading(false);
        }
      } catch (err) {
        console.error('[EarlyAccessForm] Failed to load form HTML:', err);
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load form');
          setLoading(false);
        }
      }
    };

    loadForm();

    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 4,
          gap: 2,
        }}
      >
        <CircularProgress
          size={32}
          sx={{ color: 'var(--color-secondary-light)' }}
        />
        <Typography
          sx={{
            color: 'rgba(255, 255, 255, 0.6)',
            fontSize: '0.875rem',
          }}
        >
          Loading form...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          textAlign: 'center',
          py: 4,
          px: 3,
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '12px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <Typography
          sx={{
            color: 'rgba(255, 255, 255, 0.7)',
            mb: 2,
            fontSize: '0.95rem',
          }}
        >
          Unable to load the signup form. Please try refreshing the page.
        </Typography>
        <Typography
          sx={{
            color: 'rgba(255, 255, 255, 0.5)',
            fontSize: '0.8rem',
          }}
        >
          You can also reach us directly at{' '}
          <a
            href="mailto:info@ella.fi"
            style={{
              color: 'var(--color-secondary-light)',
              textDecoration: 'underline',
            }}
          >
            info@ella.fi
          </a>
        </Typography>
      </Box>
    );
  }

  return (
    <FormErrorBoundary fallbackMessage="Unable to load the signup form">
      <Box
        sx={{
          maxWidth: '500px',
          mx: 'auto',
        }}
      >
        <ActiveCampaignEmbed htmlContent={formHtml || ''} />
      </Box>
    </FormErrorBoundary>
  );
}

export default EarlyAccessForm;
